import { readSupabaseInvokeError } from "@/lib/readSupabaseInvokeError";
import { supabase } from "@/lib/supabase";
import type { ChatEntry } from "@/types/chat";

export type VoiceSessionAuth =
  | { connectionType: "webrtc"; conversationToken: string }
  | { connectionType: "websocket"; signedUrl: string };

type VoiceSessionPayload = {
  ok?: boolean;
  error?: string;
  connectionType?: "webrtc" | "websocket";
  conversationToken?: string;
  signedUrl?: string;
};

export async function fetchVoiceSessionAuth(
  chatEntry: ChatEntry,
  connectionType: "webrtc" | "websocket" = "webrtc",
): Promise<VoiceSessionAuth> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;

  if (!token) {
    throw new Error("Sign in required to start a voice session.");
  }

  const invokeResult = await supabase.functions.invoke("elevenlabs-voice-session", {
    body: { chatEntry, connectionType },
    headers: { Authorization: `Bearer ${token}` },
  });

  const payload = invokeResult.data as VoiceSessionPayload | null;

  if (invokeResult.error) {
    const detail = await readSupabaseInvokeError(invokeResult);
    throw new Error(detail || invokeResult.error.message || "Could not reach voice session service.");
  }

  if (payload?.error) {
    throw new Error(payload.error);
  }

  if (payload?.ok && payload.connectionType === "webrtc" && payload.conversationToken) {
    return { connectionType: "webrtc", conversationToken: payload.conversationToken };
  }

  if (payload?.ok && payload.connectionType === "websocket" && payload.signedUrl) {
    return { connectionType: "websocket", signedUrl: payload.signedUrl };
  }

  throw new Error("Unexpected response from voice session service.");
}
